import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { MELDUNG_ERWEITERUNG_FEHLT, istFehlendeErweiterung } from './sifaService';
import { SifaRpcFehler, fehlerCode, rpcAntwortObjekt, rpcFeld } from './sifaPublicService';
import type { SammelLinkPublic, SifaSammelLink } from './types';

/**
 * Sammel-Link je Projekt (Nr. 143/144, docs/sifa-ausbau2-plan.md §0.5): EIN
 * QR-Code am Aushang, dahinter alle freigeschalteten Unterweisungen dieses
 * Kunden. Tabelle `sifa_sammellinks` (eine Zeile je Projekt, RLS wie Nr. 82),
 * anonym nur ueber die RPC `sifa_sammellink_public`.
 *
 * Wie beim Einzel-Link: EIN vergibt einen NEUEN Token, AUS loescht ihn — ein
 * einmal verteilter Link laesst sich so wirksam zurueckziehen, ein spaeteres
 * EIN macht den alten nicht wieder lebendig.
 *
 * FAIL-OPEN: ohne Nr. 143/144 liefert das Lesen null (kein Sammel-Link),
 * Schalten wirft Klartext.
 */

const T_SAMMELLINKS = 'sifa_sammellinks';

type Zeile = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const strOrNull = (v: unknown): string | null => (v == null ? null : str(v));
const num = (v: unknown, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};
/** Eine Zahl — oder, falls die RPC eine Liste liefert, deren Laenge. */
const anzahl = (v: unknown): number => (Array.isArray(v) ? v.length : Math.max(0, num(v, 0)));
const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

const errorCode = (e: unknown): string =>
  typeof e === 'object' && e !== null ? String((e as { code?: unknown }).code ?? '') : '';

const werfe = (e: unknown): never => {
  if (istFehlendeErweiterung(e)) throw new Error(MELDUNG_ERWEITERUNG_FEHLT);
  if (errorCode(e) === '42501') throw new Error(`Für dieses Projekt fehlt die Berechtigung. (${describeError(e)})`);
  throw new Error(describeError(e));
};

export const mapSammelLink = (row: Zeile): SifaSammelLink => ({
  projectId: str(row.project_id),
  token: str(row.token).trim() || null,
  aktivSeit: strOrNull(row.aktiv_seit),
  updatedAt: strOrNull(row.updated_at),
});

/** Der Sammel-Link des Projekts; null = noch nie angelegt (oder Nr. 143 fehlt). */
export const fetchSammelLink = async (projectId: string): Promise<SifaSammelLink | null> => {
  const pid = (projectId || '').trim();
  if (!pid) return null;
  try {
    const { data, error } = await supabase.from(T_SAMMELLINKS).select('*').eq('project_id', pid).maybeSingle();
    if (error) throw error;
    return data ? mapSammelLink(data as Zeile) : null;
  } catch (e) {
    if (istFehlendeErweiterung(e)) return null;
    return werfe(e);
  }
};

/** Firmen-Anzeigename des angemeldeten Profils — best effort, nie blockierend. */
const eigenerFirmenname = async (): Promise<string | null> => {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) return null;
    const { data } = await supabase.from('profiles').select('company_name').eq('id', userId).maybeSingle();
    const name = str((data as { company_name?: unknown } | null)?.company_name).trim();
    return name || null;
  } catch {
    return null;
  }
};

/**
 * Schaltet den Sammel-Link. Rueckgabe der gespeicherte Stand.
 *
 * EIN ueberschreibt NIE einen vorhandenen Token: sonst klickten zwei
 * Bearbeiter gleichzeitig „an", der zweite ersetzte den Token des ersten, und
 * dessen frisch gedruckter Aushang zeigte ins Leere. Deshalb in drei Schritten:
 *  (a) die Zeile anlegen, falls sie fehlt — ohne Token, `on conflict do
 *      nothing` (eine vorhandene Zeile bleibt unberuehrt);
 *  (b) den neuen Token nur setzen `where token is null`;
 *  (c) trifft (b) 0 Zeilen, hat ein anderer schon eingeschaltet: dessen Stand
 *      lesen und zurueckgeben — derselbe Link fuer alle.
 * AUS setzt den Token auf null (der Link ist danach tot; ein spaeteres EIN
 * vergibt einen neuen).
 */
export const setzeSammelLink = async (projectId: string, an: boolean): Promise<SifaSammelLink> => {
  const pid = (projectId || '').trim();
  if (!pid) throw new Error('Ohne Projekt gibt es keinen Sammel-Link.');
  const jetzt = new Date().toISOString();
  const firma = await eigenerFirmenname();

  if (!an) {
    const aus: Zeile = { project_id: pid, token: null, aktiv_seit: null, updated_at: jetzt };
    if (firma) aus.company_name = firma;
    const { data, error } = await supabase
      .from(T_SAMMELLINKS)
      .upsert(aus, { onConflict: 'project_id' })
      .select('*')
      .single();
    if (error) return werfe(error);
    return mapSammelLink(data as Zeile);
  }

  // (a) anlegen, falls fehlt — nie eine vorhandene Zeile veraendern.
  const leer: Zeile = { project_id: pid, token: null };
  if (firma) leer.company_name = firma;
  const { error: fehlerAnlegen } = await supabase
    .from(T_SAMMELLINKS)
    .upsert(leer, { onConflict: 'project_id', ignoreDuplicates: true });
  if (fehlerAnlegen) return werfe(fehlerAnlegen);

  // (b) Token nur dort, wo noch keiner ist.
  const ein: Zeile = { token: crypto.randomUUID(), aktiv_seit: jetzt, updated_at: jetzt };
  if (firma) ein.company_name = firma;
  const { data, error } = await supabase
    .from(T_SAMMELLINKS)
    .update(ein)
    .eq('project_id', pid)
    .is('token', null)
    .select('*');
  if (error) return werfe(error);
  if (Array.isArray(data) && data.length > 0) return mapSammelLink(data[0] as Zeile);

  // (c) schon eingeschaltet (anderer Browser) — dessen Link gilt.
  const vorhanden = await fetchSammelLink(pid);
  if (vorhanden?.token) return vorhanden;
  throw new Error('Der Sammel-Link konnte nicht eingeschaltet werden — fehlt die Berechtigung für dieses Projekt?');
};

/** Ursprung der App fuer Links — im Browser die aktuelle Adresse, sonst leer. */
const hierOrt = (): { origin: string; pathname: string } =>
  typeof window !== 'undefined' && window.location
    ? { origin: window.location.origin, pathname: window.location.pathname }
    : { origin: '', pathname: '/' };

/**
 * Die Adresse fuer QR und Aushang: `<origin>/#/unterweisungen/<token>`.
 * `pathname` bleibt wie beim Einzel-Link (linkUrl) drin, weil die App nicht
 * zwingend an der Wurzel liegt; `ort` nur fuer Tests.
 */
export const sammelLinkUrl = (token: string, ort?: { origin: string; pathname?: string }): string => {
  const o = ort ?? hierOrt();
  return `${o.origin}${o.pathname || '/'}#/unterweisungen/${encodeURIComponent((token || '').trim())}`;
};

/**
 * Die Liste fuer die anonyme Seite (RPC `sifa_sammellink_public`): nur aktive
 * Unterweisungen mit eingeschaltetem, nicht abgelaufenem Link — das filtert
 * die Datenbank. null = Token unbekannt, abgeschaltet oder Nr. 144 fehlt.
 * Eine Zeile ohne Link-Token waere nicht startbar und faellt heraus.
 */
export const ladeSammelLinkPublic = async (token: string): Promise<SammelLinkPublic | null> => {
  const t = (token || '').trim();
  if (!t) return null;
  try {
    const { data, error } = await supabase.rpc('sifa_sammellink_public', { p_token: t });
    if (error) throw error;
    const row = rpcAntwortObjekt(data);
    if (!row) return null;
    return {
      kundeName: str(rpcFeld(row, 'kundeName', 'kunde_name')),
      bueroName: str(rpcFeld(row, 'bueroName', 'buero_name')),
      unterweisungen: arr<Zeile>(row.unterweisungen)
        .map((u) => {
          const dauer = rpcFeld(u, 'dauerMin', 'dauer_min');
          return {
            titel: str(u.titel),
            beschreibung: strOrNull(u.beschreibung),
            dauerMin: dauer == null ? null : num(dauer, 0),
            gueltigMonate: num(rpcFeld(u, 'gueltigMonate', 'gueltig_monate'), 12),
            abschnitte: anzahl(rpcFeld(u, 'abschnitte', 'inhalt')),
            fragen: anzahl(u.fragen),
            linkToken: str(rpcFeld(u, 'linkToken', 'link_token')).trim(),
          };
        })
        .filter((u) => u.linkToken),
    };
  } catch (e) {
    if (istFehlendeErweiterung(e)) return null;
    throw new SifaRpcFehler(describeError(e), fehlerCode(e));
  }
};
